import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Typography,
  useTheme,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AxiosService } from "../../services/axios/axiosService";

// Helper to format seconds as M:SS (e.g. 3:45)
const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "0:00";
  const total = Math.max(0, Math.floor(Number(seconds)));
  const mins = Math.floor(total / 60);
  const secs = (total % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

const getStatusMeta = (response) => {
  const duration = response?.responseDurationSeconds ?? 0;
  const text = response?.transcribedText || "";

  const isNoSpeech =
    typeof text === "string" &&
    /(No speech could be recognized|No speech|Recognition timeout)/i.test(text);

  const hasSpeech = !isNoSpeech && text.trim().length > 0;

  if (duration > 0 && hasSpeech) {
    return {
      label: "Answered",
      color: "#059669",
      bg: "#a8f2c18f",
      border: "#22c55e",
    };
  }

  return {
    label: "Not answered",
    color: "#dc2626",
    bg: "#fee2e2",
    border: "#fca5a5",
  };
};

let lastAutoPlayedKey = null;

const InterviewQuestionsCard = ({
  loading,
  error,
  questions,
  totalQuestions,
  selectedResponseId,
  onSelectResponse,
  onTtsEnd,
  onTtsStart,
  registerStopTts,
}) => {
  const theme = useTheme();
  const responses = Array.isArray(questions) ? questions : [];
  const answeredCount = responses.filter(
    (resp) => getStatusMeta(resp).label === "Answered",
  ).length;
  const pageSize = 3;
  const [page, setPage] = React.useState(0);
  const totalPages = Math.max(1, Math.ceil(responses.length / pageSize));

  const audioRef = React.useRef(null);
  const autoPlayedRef = React.useRef(false);
  const ttsAbortControllerRef = React.useRef(null);
  const ttsLatestRequestIdRef = React.useRef(null);
  const [ttsPlayingResponseId, setTtsPlayingResponseId] = React.useState(null);

  const stopTts = React.useCallback(() => {
    if (ttsAbortControllerRef.current) {
      ttsAbortControllerRef.current.abort();
      ttsAbortControllerRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    ttsLatestRequestIdRef.current = null;
    setTtsPlayingResponseId(null);
  }, []);

  React.useEffect(() => {
    if (typeof registerStopTts === "function") {
      registerStopTts(stopTts);
    }
  }, [registerStopTts, stopTts]);

  const callTextToSpeech = React.useCallback(
    async (text, responseId) => {
      if (!text) return;

      if (typeof onTtsStart === "function") {
        onTtsStart();
      }

      if (ttsAbortControllerRef.current) {
        ttsAbortControllerRef.current.abort();
        ttsAbortControllerRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }

      const requestId = responseId ?? Symbol("tts");
      ttsLatestRequestIdRef.current = requestId;
      setTtsPlayingResponseId(responseId ?? null);

      const controller = new AbortController();
      ttsAbortControllerRef.current = controller;

      try {
        const body = { Text: text };
        const response = await AxiosService.post(
          `/api/Interview/text-to-speech`,
          body,
          { responseType: "blob", signal: controller.signal }
        );

        const audioUrl = window.URL.createObjectURL(response);
        if (ttsLatestRequestIdRef.current !== requestId) {
          URL.revokeObjectURL(audioUrl);
          return;
        }
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        audio.onended = () => {
          ttsLatestRequestIdRef.current = null;
          setTtsPlayingResponseId(null);
          if (typeof onTtsEnd === "function") {
            onTtsEnd();
          }
        };

        await audio.play();
      } catch (err) {
        if (controller.signal?.aborted) return;
        if (ttsLatestRequestIdRef.current === requestId) {
          ttsLatestRequestIdRef.current = null;
          setTtsPlayingResponseId(null);
          if (typeof onTtsEnd === "function") {
            onTtsEnd();
          }
        }
      } finally {
        if (ttsAbortControllerRef.current === controller) {
          ttsAbortControllerRef.current = null;
        }
      }
    },
    [onTtsEnd, onTtsStart]
  );

  React.useEffect(() => {
    setPage(0);
    autoPlayedRef.current = false;
  }, [questions]);

  React.useEffect(() => {
    return () => {
      if (ttsAbortControllerRef.current) {
        ttsAbortControllerRef.current.abort();
        ttsAbortControllerRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      ttsLatestRequestIdRef.current = null;
      lastAutoPlayedKey = null;
    };
  }, []);

  React.useEffect(() => {
    if (loading || responses.length === 0) return;
    const firstResponse = responses[0];
    const sessionKey = firstResponse?.id ?? firstResponse?.question?.id ?? "";
    if (lastAutoPlayedKey === sessionKey) return;
    lastAutoPlayedKey = sessionKey;
    autoPlayedRef.current = true;
    if (!selectedResponseId) {
      onSelectResponse?.(firstResponse);
    }
    const questionText = firstResponse?.question?.questionText;
    if (questionText) callTextToSpeech(questionText, firstResponse?.id);
  }, [loading, responses, callTextToSpeech]);

  const startIndex = page * pageSize;
  const endIndex = startIndex + pageSize;
  const visibleResponses = responses.slice(startIndex, endIndex);
  const isEmptyState = !loading && !error && responses.length === 0;

  // Reusable tooltip styles
  const tooltipSx = {
    bgcolor: "#374151",
    color: "#e5e7eb",
    fontSize: "0.7rem",
    boxShadow: "0 4px 12px rgba(15,23,42,0.35)",
  };

  // Reusable info icon renderer — always shown, falls back to "No feedback"
  const renderInfoIcon = (feedbackText) => (
    <Tooltip
      title={
        <Typography variant="caption">
          {feedbackText || "No feedback"}
        </Typography>
      }
      arrow
      placement="top"
      componentsProps={{ tooltip: { sx: tooltipSx } }}
    >
      <Box
        component="span"
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "help",
          "& svg": { fontSize: 16 },
        }}
      >
        <InfoOutlinedIcon fontSize="inherit" />
      </Box>
    </Tooltip>
  );

  const renderedResponses =
    !loading && !error
      ? visibleResponses.map((response, index) => {
          const question = response?.question || {};
          const absoluteIndex = startIndex + index;
          const number = absoluteIndex + 1;
          const text = question.questionText || "Untitled question";
          const seconds = response.responseDurationSeconds ?? 0;
          const durationLabel = formatDuration(seconds);
          const statusMeta = getStatusMeta(response);

          const rawBehaviorScore =
            response?.behaviorScore ??
            response?.behaviourScore ??
            response?.behavioralScore ??
            null;
          const rawAiScore =
            response?.aiResponse ??
            response?.answereScore ??
            response?.contentScore ??
            null;
          const behaviorScore =
            rawBehaviorScore != null && !Number.isNaN(Number(rawBehaviorScore))
              ? Math.round(Number(rawBehaviorScore) * 10) / 10
              : 0;
          const aiScore =
            rawAiScore != null && !Number.isNaN(Number(rawAiScore))
              ? Math.round(Number(rawAiScore) * 10) / 10
              : 0;

          const behaviorFeedback =
            response?.behaviorFeedback ??
            response?.behaviourFeedback ??
            response?.behavioralNotes ??
            null;
          const aiFeedback =
            response?.aiResponseFeedback ?? response?.contentFeedback ?? null;

          const questionLabel = `QUESTION ${String(number).padStart(2, "0")}`;
          const isSelected =
            selectedResponseId != null && response?.id === selectedResponseId;
          const isTtsPlaying =
            ttsPlayingResponseId != null &&
            response?.id === ttsPlayingResponseId;
          const isAnswered = statusMeta.label === "Answered";

          const neutralBorderColor = theme.palette.divider;
          const selectedBorderColor = isAnswered ? "#059669" : "#dc2626";
          const borderColor = isSelected ? selectedBorderColor : neutralBorderColor;
          const borderWidth = isSelected ? 2 : 1;

          const baseBackground =
            theme.palette.mode === "dark" ? "rgba(15,23,42,0.6)" : "#f9fafb";
          const selectedBackground =
            theme.palette.mode === "dark"
              ? isAnswered
                ? "rgba(22,163,101,0.22)"
                : "rgba(220,38,38,0.22)"
              : isAnswered
              ? "rgba(22,163,101,0.08)"
              : "rgba(220,38,38,0.08)";
          const cardBackground = isSelected ? selectedBackground : baseBackground;

          const baseQuestionColor = theme.palette.text.secondary;
          const questionColor = isSelected
            ? isAnswered
              ? "#059669"
              : "#dc2626"
            : baseQuestionColor;

          return (
            <Box
              key={response?.id ?? absoluteIndex}
              onClick={() => {
                onSelectResponse?.(response);
                callTextToSpeech(text, response?.id);
              }}
              role="button"
              tabIndex={0}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                mb: 1,
                p: 2,
                borderRadius: 4,
                cursor: onSelectResponse ? "pointer" : "default",
                border: `${borderWidth}px solid ${borderColor}`,
                backgroundColor: cardBackground,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      fontSize: 11,
                      color: questionColor,
                      textTransform: "uppercase",
                    }}
                  >
                    {questionLabel}
                  </Typography>
                  <Typography
                    sx={{ color: theme.palette.text.secondary, fontSize: 11 }}
                  >
                    {durationLabel}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ overflowWrap: "anywhere", letterSpacing: 0.2 }}
                >
                  {text}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mt: 0.25,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 0.75,
                      minWidth: 0,
                    }}
                  >
                    <Chip
                      label={statusMeta.label}
                      size="small"
                      sx={{
                        fontSize: 11,
                        backgroundColor: statusMeta.bg,
                        color: statusMeta.color,
                        fontWeight: 500,
                        flexShrink: 0,
                        cursor: "default",
                      }}
                    />
                    {behaviorScore != null && (
                      <Chip
                        icon={renderInfoIcon(behaviorFeedback)}
                        label={`Behavior Score: ${behaviorScore}/5`}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          backgroundColor: "#e6e62547",
                          color: "#8d7c0aed",
                          cursor: "default",
                        }}
                      />
                    )}
                    {aiScore != null && (
                      <Chip
                        icon={renderInfoIcon(aiFeedback)}
                        label={`AI Response Score: ${aiScore}/5`}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          backgroundColor: "rgba(129,140,248,0.16)",
                          color: "#4f46e5",
                          cursor: "default",
                        }}
                      />
                    )}
                  </Box>
                  <Tooltip
                    title={isTtsPlaying ? "Stop speech" : "Play question (TTS)"}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isTtsPlaying) {
                          stopTts();
                        } else {
                          onSelectResponse?.(response);
                          callTextToSpeech(text, response?.id);
                        }
                      }}
                      sx={{
                        p: 0.25,
                        color: isTtsPlaying
                          ? "error.main"
                          : theme.palette.primary.main,
                      }}
                      aria-label={
                        isTtsPlaying ? "Stop speech" : "Play question"
                      }
                    >
                      {isTtsPlaying ? (
                        <StopIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <VolumeUpIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          );
        })
      : null;

  return (
    <Card
      elevation={2}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
            : "0px 1px 3px rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}
          >
            IGNA Scout Questions
          </Typography>
        </Box>

        <Box
          sx={{
            minHeight: 320,
            display: isEmptyState ? "flex" : "block",
            alignItems: isEmptyState ? "center" : "stretch",
            justifyContent: isEmptyState ? "center" : "flex-start",
          }}
        >
          {loading && (
            <Box>
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
                </Box>
              ))}
            </Box>
          )}

          {!loading && error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}

          {!loading && !error && responses.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              No question details available.
            </Typography>
          )}

          {renderedResponses}
        </Box>

        {!loading && !error && responses.length > pageSize && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 3,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Typography variant="caption" color="text.secondary">
              Page {page + 1} of {totalPages}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default InterviewQuestionsCard;